/**
 * The `BooksSourceProvider` seam's value types (piece 2; decision 0920): what
 * an old accounting system's books answer, in our own shapes. 🔨 Our types in,
 * our types out: the Zoho Books adapter validates every answer into these, an
 * absent field reading as empty, and no vendor field name passes it.
 */

/** One page of a list: its rows, its number, and whether another follows. */
export interface BooksPage<T> {
  rows: T[];
  page: number;
  hasMore: boolean;
}

export interface BooksOrganisation {
  id: string;
  name: string;
  /** An IANA zone; a zone the source names that is not one reads as empty. */
  timeZone: string | null;
  currencyCode: string | null;
}

export interface BooksAccount {
  id: string;
  name: string;
  /** The source's account code, trimmed; empty where it carries none. */
  code: string | null;
  /** The source's own account type, lower-cased, read through the mapping table of piece 31. */
  sourceType: string;
  isActive: boolean;
  parentId: string | null;
  description: string | null;
  modifiedAt: Date | null;
}

export interface BooksTax {
  id: string;
  name: string;
  /** A percentage: 6 is six percent. */
  percentage: number;
  kind: string | null;
  isActive: boolean;
}

export interface BooksContactSummary {
  id: string;
  name: string;
  modifiedAt: Date | null;
}

export interface BooksAddress {
  /** The source's own id for the address; empty where it carries none, and the contact's id and the address's role stand in. */
  id: string | null;
  attention: string | null;
  lines: string[];
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface BooksContactPerson {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  title: string | null;
  /** 🔒 The person's department as Zoho Books holds it, free text (decision 8100); null where none or blank, and absent on a person staged before. */
  department?: string | null;
  isPrimary: boolean;
}

/** A populated source field with no home here, which becomes a custom-field definition (decision 0233). */
export interface BooksUnhomedField {
  label: string;
  value: string;
}

export interface BooksContact {
  id: string;
  name: string;
  companyName: string | null;
  isCustomer: boolean;
  isVendor: boolean;
  /** 🔒 A Zoho CRM manufacturer handed to the Contacts block (decision 8380), whose company is flagged Manufacturer; absent on every Zoho Books contact, so its hash is unchanged. */
  isManufacturer?: boolean;
  isActive: boolean;
  website: string | null;
  phone: string | null;
  paymentTerms: string | null;
  currencyCode: string | null;
  /** 🔒 A customer's credit limit in whole cents, in the contact's `currencyCode` (decision 8000); null where Zoho holds none, zero or a vendor, and absent on a contact staged before. */
  creditLimit?: number | null;
  /** 🔒 The contact's notes, free text (decision 8100); null where none or blank, and absent on a contact staged before. */
  notes?: string | null;
  taxNumber: string | null;
  /** 🔒 The contact's DUNS number, read off a custom field labeled DUNS (decision 3082); absent where it carries none, and on a contact staged before. */
  dunsNumber?: string | null;
  billingAddress: BooksAddress | null;
  shippingAddress: BooksAddress | null;
  persons: BooksContactPerson[];
  unhomed: BooksUnhomedField[];
  modifiedAt: Date | null;
  /**
   * The calendar day, `YYYY-MM-DD`, the source created the contact, in the
   * organization's own zone; empty where it carries none (decision 1627). Its
   * people's employments start on it; a contact person carries no date of its own.
   */
  createdOn?: string | null;
}

export interface BooksItem {
  id: string;
  name: string;
  sku: string | null;
  manufacturer: string | null;
  partNumber: string | null;
  description: string | null;
  purchaseDescription: string | null;
  /** Whole cents, rounded half up at the adapter. */
  rateCents: number;
  purchaseRateCents: number;
  /** The source's unit text exactly as it carries it, trimmed; empty is the empty string. */
  unit: string;
  isService: boolean;
  isActive: boolean;
  salesAccountId: string | null;
  purchaseAccountId: string | null;
  modifiedAt: Date | null;
  /**
   * 🔒 The item read whole (decision 1676): its populated fields no field row
   * reads, each a label and a value. Each detail below is left out where it is
   * empty, so an item carrying none keeps the content hash it had before.
   */
  unhomed?: BooksUnhomedField[];
  /** The item's tax as the source names it. */
  taxName?: string;
  /** Whether the source marks the item taxable, where it says; the United States edition carries no tax name. */
  sourceTaxable?: boolean;
  /** The item's preferred vendor: its contact id, which block 2 linked, and its name. */
  preferredVendorId?: string;
  preferredVendorName?: string;
  /**
   * 🔒 The item's picture as Zoho Inventory names it (decision 3661), read once
   * per list page. It stands outside the item's content hash, and it is left
   * out where the item carries none.
   */
  image?: BooksItemImage;
  /** 🔒 Zoho Inventory's words and the remedy where it refused the page's image attributes (decision 3661); left out where it answered. */
  imageRefusal?: string;
  /**
   * 🔒 The item's stock (decision 8260), each outside the content hash and left out where the source names none, so an
   * item staged before keeps its shape: its stock on hand summed over its locations, its reorder level, its opening stock
   * and that stock's unit price in whole cents, and its inventory account's id.
   */
  stockOnHand?: number;
  reorderLevel?: number;
  initialStock?: number;
  initialStockRateCents?: number;
  inventoryAccountId?: string;
  /** 🔒 Zoho Inventory's words and the remedy where it refused the page's stock (decision 8260); left out where it answered. */
  stockRefusal?: string;
  /**
   * 🔒 Whether Zoho tracks the item's stock (decision 10820): true where its `item_type` reads `inventory`, false where it
   * names another type. Outside the content hash, and left out where Zoho names no type, which reads as tracked.
   */
  inventoryTracked?: boolean;
}

/**
 * 🔒 One item's stock as Zoho Inventory's bulk item details answer it (decision 8260): the item's id, its
 * `location_stock_on_hand` summed over its `locations`, its `reorder_level`, and whether its `status` is active.
 */
export interface BooksItemStock {
  itemId: string;
  /** Null where the answer names no count for the item at all, which a true-up passes over rather than reading as none. */
  stockOnHand: number | null;
  reorderLevel: number | null;
  isActive: boolean;
  /** 🔒 Whether Zoho tracks the item's stock, from its `item_type` (decision 10820); left out where the answer names no type. */
  inventoryTracked?: boolean;
}

/**
 * 🔒 One item's picture as Zoho Inventory's bulk item details name it
 * (decision 3661): the item's id, `image_id`, `image_name` and `image_type`.
 * The image id is the picture's own, so a changed picture carries a new one.
 */
export interface BooksItemImage {
  itemId: string;
  id: string;
  name: string | null;
  /** Zoho's format word, as `jpg`; the stored type is the image read's own `Content-Type`. */
  type: string | null;
}

/** The two answers an items read's key probe asks for (decision 1675). */
export const BOOKS_KEY_PROBE_ANSWERS = ['BooksItem', 'InventoryItem'] as const;
export type BooksKeyProbeAnswer = (typeof BOOKS_KEY_PROBE_ANSWERS)[number];

/**
 * 🔒 The key names one answer carried for one item, read whole (decision
 * 1675): names only, never a value. A nested key reads `parent.child` and a key
 * inside a list `parent[].child`. A refused call keeps its sentence instead.
 * 🔨 This is the one place a source's own field names leave the adapter, as
 * evidence a person reads; no service converts them.
 */
export interface BooksKeyProbe {
  answer: BooksKeyProbeAnswer;
  itemId: string;
  keys: string[];
  refusal: string | null;
}

/**
 * Block 4's document kinds, in the order a run reads them, which is the order
 * of a sale (piece 31; decisions 0930, 3340): an estimate before the sales
 * order made from it, a sales order before its invoice and its purchase order,
 * a payment after the invoice it settles, and a bill after its purchase order.
 * 🔨 A kind joins this list in the place its own source sets, and nothing else
 * reads the order from anywhere but here.
 */
export const BOOKS_DOCUMENT_KINDS = ['Estimate', 'SalesOrder', 'Invoice', 'CreditNote', 'CustomerPayment', 'PurchaseOrder', 'Bill', 'VendorCredit', 'VendorPayment', 'Expense', 'ManualJournal'] as const;
export type BooksDocumentKind = (typeof BOOKS_DOCUMENT_KINDS)[number];

/** 🔒 The three kinds that post nothing, read so a sale's documents arrive whole and linked (decision 3340). */
export const BOOKS_UNPOSTED_DOCUMENT_KINDS: readonly BooksDocumentKind[] = ['Estimate', 'SalesOrder', 'PurchaseOrder'];

/**
 * 🔒 The kind each kind came from in a sale (decision 3341): a sales order
 * from its estimate, an invoice and a purchase order from their sales order,
 * and a bill from its purchase order. Every kind it names is read before the
 * kind that names it, so the source is here first.
 */
export const BOOKS_DOCUMENT_SOURCE_KIND: Readonly<Partial<Record<BooksDocumentKind, BooksDocumentKind>>> = {
  SalesOrder: 'Estimate', Invoice: 'SalesOrder', PurchaseOrder: 'SalesOrder', Bill: 'PurchaseOrder',
};

/** One document another came from (decision 3341): the source's own id, and its number where the source gives one. */
export interface BooksDocumentSource {
  id: string;
  number: string | null;
}

/** The source's status, read at the adapter as one of three: a draft posts nothing, a void document is not pulled, and every other status is posted (decision 0930). */
export const BOOKS_DOCUMENT_STATUSES = ['Draft', 'Posted', 'Void'] as const;
export type BooksDocumentStatus = (typeof BOOKS_DOCUMENT_STATUSES)[number];

/** The reporting check's reports, the source's own statements beside ours (decision 0933). */
export const BOOKS_REPORT_KINDS = ['ProfitAndLoss', 'BalanceSheet'] as const;
export type BooksReportKind = (typeof BOOKS_REPORT_KINDS)[number];

/** A line's discount, as the source carries it: a percentage of the line, or an amount off it in whole cents. */
export type BooksLineDiscount = { kind: 'Percentage'; percent: number } | { kind: 'Flat'; cents: number };

export interface BooksDocumentLine {
  id: string | null;
  /** The source's item id, where the line sells or buys one. */
  itemId: string | null;
  /** The source's account id the line posts to. */
  accountId: string | null;
  name: string | null;
  description: string | null;
  quantity: number;
  /** Whole cents, rounded half up at the adapter. */
  rateCents: number;
  discount: BooksLineDiscount | null;
  /** The line's amount before tax, whole cents. A journal line's is signed: a debit above zero, a credit below. */
  amountCents: number;
  taxId: string | null;
  taxCents: number;
  /** A journal line's contact, which a line on a control account names. */
  contactId: string | null;
}

/** One document a payment or a credit settles, and how much of it. */
export interface BooksApplication {
  documentId: string;
  amountCents: number;
}

/**
 * One document of block 4 (decision 0930), read whole: its lines, its totals
 * and what it settles. Every amount is whole cents and every date a calendar
 * day, `YYYY-MM-DD`, as the source dates it.
 */
export interface BooksDocument {
  id: string;
  kind: BooksDocumentKind;
  number: string | null;
  date: string | null;
  /** The day it falls due; an estimate's expiry day and a purchase order's delivery day (decision 3340). */
  dueDate: string | null;
  /**
   * 🔒 When Zoho created it (decision 10763; the owner's answer Z4b), its `created_time` as an instant, which orders the
   * documents read. Absent where the source names none, and outside the content hash.
   */
  createdAt?: string;
  status: BooksDocumentStatus;
  /** The source's own status word, lower-cased, for the row's notes. */
  sourceStatus: string | null;
  /** The customer's or vendor's source id; empty on a journal, whose lines name theirs. */
  contactId: string | null;
  /** The contact's name as the source spells it, for a row's reason when no pull linked the contact. */
  contactName: string | null;
  reference: string | null;
  paymentTerms: string | null;
  lines: BooksDocumentLine[];
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  balanceCents: number;
  /**
   * 🔒 The discount Zoho takes off the whole of an invoice, an estimate, a sales order or a credit note (decision 11920), or of
   * a bill, a purchase order or a vendor credit (decision 12042):
   * Zoho's percent or a flat amount, the amount in whole cents as Zoho states it, and whether it comes off before tax.
   * Absent where the document carries none or its discounts are its lines' own, and outside the content hash.
   */
  discount?: BooksLineDiscount & { cents: number; beforeTax: boolean; itemLevelFallback?: true };
  /** 🔒 The discount fields Zoho sent, each as the reader saw it or `absent` (decision 12320); read for the total-differs note alone, and outside the content hash. */
  discountFields?: { discount_type: string; discount: string; discount_amount: string; is_discount_before_tax: string };
  /**
   * 🔒 A bill's or purchase order's adjustment in cents (T2853), and an invoice's, estimate's, sales order's or credit note's
   * (decision 12480), signed as Zoho sends it; absent where the source names none, and outside the content hash.
   */
  adjustmentCents?: number;
  /** 🔒 A sale's adjustment words as Zoho gives them, its `adjustment_description` (decision 12480); absent where none, and outside the content hash. */
  adjustmentDescription?: string;
  /** 🔒 The Zoho account a sale's adjustment posts to, its `adjustment_account_id` (decision 12480); absent where none, and outside the content hash. */
  adjustmentAccountId?: string;
  /** 🔒 That account's name as Zoho spells it, its `adjustment_account_name` (decision 12480), for the row's note; absent where none, and outside the content hash. */
  adjustmentAccountName?: string;
  /** 🔒 A bill's, purchase order's or sale's shipping charge in cents (T2853, decision 12480); absent where the source names none, and outside the content hash. */
  shippingCents?: number;
  /**
   * 🔒 What an invoice has written off in Zoho Books, in cents (decision 8900; the Invoices reference's `write_off_amount`), absent where
   * none; outside the content hash, so an invoice written before it was read is unchanged.
   */
  writeOffCents?: number;
  /** 🔒 The day Zoho wrote it off, where the invoice's answer names one; the reference names no such field (decision 8900), so it is absent until a live answer shows it. */
  writeOffDate?: string | null;
  /** The invoices a customer payment or a credit note settles, the bills a vendor payment or a vendor credit does. */
  applications: BooksApplication[];
  /** The one invoice a credit note credits, where it names one. */
  creditedDocumentId: string | null;
  /** The account the money moved through: a payment's deposit or paid-through account, an expense's paid-through account. */
  accountId: string | null;
  /** 🔒 That account's name as the source spells it, for a row's reason when no pull linked it (decision 3262); absent where the source names none, and outside the content hash. */
  accountName?: string | null;
  /** A payment's mode, lower-cased. */
  method: string | null;
  /** The source's id for the shipping address an invoice names, which block 2 linked to a site. */
  shippingAddressId: string | null;
  /** 🔒 The billing address an invoice or a bill carries (decision 3263): an invoice whose customer has no site here opens one at it. Absent where the source gives none, and outside the content hash. */
  billingAddress?: BooksAddress | null;
  /** 🔒 The shipping address an invoice, an estimate or a sales order carries as Zoho holds it (decision 11500): the document ships to the site at it. Absent where the source gives none, and outside the content hash. */
  shippingAddress?: BooksAddress | null;
  /**
   * 🔒 The documents this one came from, of the kind `BOOKS_DOCUMENT_SOURCE_KIND`
   * names (decision 3341), in the order the source lists them. Absent where the
   * source names none, and outside the content hash.
   */
  fromDocuments?: BooksDocumentSource[];
  /**
   * 🔒 The files the document carries, as its whole answer lists them (decision
   * 3480), in the source's order. Absent where it lists none, and outside the
   * content hash, so a document hashed before keeps its hash.
   */
  attachments?: BooksAttachment[];
  /** A journal's reporting basis. */
  basis: 'both' | 'accrual' | 'cash' | null;
  memo: string | null;
  modifiedAt: Date | null;
}

/** 🔒 One file a document lists (decision 3480): the source's own attachment id and the file's name. */
export interface BooksAttachment {
  id: string;
  name: string;
}

/** 🔒 One attachment's bytes as the source answered them (decision 3480): its type is the answer's own, and its size the bytes received. */
export interface BooksAttachmentFile {
  mimeType: string;
  bytes: Uint8Array;
}

/**
 * 🔒 How each kind's files are read (decision 3480, from the reference read
 * 2026-10-12): `Document` reads one listed file by its id, `Attachment` reads
 * the one file the document answers. A kind not named here offers no read, so
 * its files stay in the source and the row names them.
 */
export const BOOKS_ATTACHMENT_READS: Readonly<Partial<Record<BooksDocumentKind, 'Document' | 'Attachment'>>> = {
  Invoice: 'Document', CreditNote: 'Document', Bill: 'Attachment', SalesOrder: 'Attachment', PurchaseOrder: 'Attachment',
};

/** The Books document types the attachment mapping asks about, one answer each (decision 3480), in the order of a sale. */
export const BOOKS_ATTACHMENT_KINDS: readonly BooksDocumentKind[] = BOOKS_DOCUMENT_KINDS.filter((kind) => BOOKS_ATTACHMENT_READS[kind] !== undefined);

/**
 * 🔒 The kinds a documents read loads as open at the cut-off (decisions 2991,
 * 3481): the invoices, the credit notes and the customer payments not yet
 * applied on the receivable side, and the bills and vendor credits on the
 * payable side.
 */
export const BOOKS_OPEN_DOCUMENT_KINDS = ['Invoice', 'CreditNote', 'CustomerPayment', 'Bill', 'VendorCredit'] as const;
export type BooksOpenDocumentKind = (typeof BOOKS_OPEN_DOCUMENT_KINDS)[number];

/**
 * 🔒 One document the source held open at the end of the cut-off day
 * (decisions 2991, 3481), read whole: the document as block 4 reads it, and
 * what it still held that day. An invoice's or a bill's is what it still
 * owed, a credit's what it had still to give, and a payment's what it had
 * still to apply.
 */
export interface BooksOpenDocument extends BooksDocument {
  /** Whole cents still open at the end of the cut-off day; above zero on every row the seam answers. */
  openCents: number;
}

/**
 * 🔒 One inventory adjustment Zoho Inventory answers (decision 3660, from the
 * reference read 2026-10-12): the stock it counted up or down outside a sale or
 * a purchase. A documents read reads them after the sale's documents, and each
 * quantity line becomes one Adjustment stock movement on its item.
 */
export interface BooksInventoryAdjustment {
  id: string;
  /** The adjustment's calendar day, `YYYY-MM-DD`, as the source dates it; empty where it carries none. */
  date: string | null;
  reason: string | null;
  description: string | null;
  reference: string | null;
  /** The source's own kind, lower-cased: `quantity` moves stock, `value` revalues it and moves none. */
  adjustmentType: string | null;
  lines: BooksInventoryAdjustmentLine[];
}

/** One line of an inventory adjustment: the item it counted and by how much. */
export interface BooksInventoryAdjustmentLine {
  /** The source's line id; empty where it carries none, and the line's place stands in. */
  id: string | null;
  itemId: string | null;
  name: string | null;
  /** Signed as the source counted it: above zero counted up, below zero counted down. */
  quantity: number;
  /** The line's total in whole cents, unsigned, rounded half up at the adapter. */
  totalCents: number;
  /**
   * 🔒 Decision 10250: on a value adjustment's line alone, its value in whole cents with the sign the source sends, a
   * decrease below zero. Left out of a quantity line, so a quantity adjustment's content hash is what it was.
   */
  valueCents?: number;
}

/** The sections the reporting check compares (decision 0933), each report's in the order it prints. */
export const BOOKS_REPORT_SECTIONS = ['operatingIncome', 'costOfGoodsSold', 'operatingExpense', 'otherIncome', 'otherExpense', 'netProfit', 'assets', 'liabilities', 'equity'] as const;
export type BooksReportSection = (typeof BOOKS_REPORT_SECTIONS)[number];

export const BOOKS_REPORT_SECTIONS_OF: Readonly<Record<BooksReportKind, readonly BooksReportSection[]>> = {
  ProfitAndLoss: ['operatingIncome', 'costOfGoodsSold', 'operatingExpense', 'otherIncome', 'otherExpense', 'netProfit'],
  BalanceSheet: ['assets', 'liabilities', 'equity'],
};

/** One account line of a source report: its account's source id, where it names one, the section it stands in and its amount in that section's own sign. */
export interface BooksReportAccount {
  accountId: string | null;
  name: string;
  section: BooksReportSection;
  amountCents: number;
}

/** A source statement (decision 0933): the section totals in whole cents, each in its section's own sign, and the account lines beneath them. */
export interface BooksReport {
  kind: BooksReportKind;
  from: string;
  to: string;
  sections: { key: BooksReportSection; label: string; amountCents: number; /** The node path the figure was read from, the names from the tree's top down (T3484). */ path?: string }[];
  accounts: BooksReportAccount[];
  /** 🔒 The source's answer as it came, kept so a person can download it beside the comparison (T3484). */
  raw?: unknown;
}

/**
 * 🔒 The document kinds whose Zoho Books list takes `last_modified_time` as
 * *modified after*, in the reference's own words (decision 3801, read
 * 2026-10-12): estimates, invoices, credit notes, purchase orders, bills and
 * vendor credits. The sales orders, vendor payments and journals name the
 * parameter without saying what it matches, and the customer payments and
 * expenses do not take it, so the daily pull reads those by date alone.
 */
export const BOOKS_CHANGED_SINCE_KINDS: readonly BooksDocumentKind[] = ['Estimate', 'Invoice', 'CreditNote', 'PurchaseOrder', 'Bill', 'VendorCredit'];

/**
 * The seam (piece 2; decision 0920). `documents` and
 * `report` serve block 4 and the reporting check (decisions 0930, 0933),
 * `openDocuments` the documents open at the cut-off (decision 2991),
 * `attachment` the files a document lists (decision 3480), and
 * `inventoryAdjustments` the stock Zoho counted up or down (decision 3660),
 * and `itemImages` with `itemImage` an item's picture (decision 3661), and
 * `itemStock` an item's stock (decision 8260).
 * `bankAccounts` lists the bank and card accounts beside the chart (decision 4800).
 */
export interface BooksSourceProvider {
  organisation(): Promise<BooksOrganisation>;
  accounts(page: number): Promise<BooksPage<BooksAccount>>;
  /**
   * 🔒 One page of the bank and credit card accounts, whatever their status
   * (decision 4800): Zoho keeps some apart from the chart, so block 1 reads
   * them after it. Each is read as a chart account is.
   */
  bankAccounts(page: number): Promise<BooksPage<BooksAccount>>;
  taxes(page: number): Promise<BooksPage<BooksTax>>;
  contacts(page: number): Promise<BooksPage<BooksContactSummary>>;
  contact(id: string): Promise<BooksContact>;
  items(page: number): Promise<BooksPage<BooksItem>>;
  /** 🔒 One item read whole by its id, with its details (decision 1676). */
  item(id: string): Promise<BooksItem>;
  /** 🔒 One item's key names from each service (decision 1675); a source with nothing to probe leaves it out. */
  itemKeys?(id: string): Promise<BooksKeyProbe[]>;
  documents(kind: BooksDocumentKind, from: string, to: string, page: number): Promise<BooksPage<BooksDocument>>;
  /**
   * 🔒 One page of a kind's documents modified after the instant, whatever their
   * date, each read whole (decision 3801). Asked only of the kinds
   * `BOOKS_CHANGED_SINCE_KINDS` names; a source that cannot ask leaves it out,
   * and the daily pull then reads those kinds by date alone.
   */
  changedDocuments?(kind: BooksDocumentKind, since: Date, page: number): Promise<BooksPage<BooksDocument>>;
  report(kind: BooksReportKind, from: string, to: string): Promise<BooksReport>;
  /** 🔒 The documents of a kind dated on or before the cut-off and still open at its end (decisions 2991, 3481), page by page. */
  openDocuments(kind: BooksOpenDocumentKind, cutoff: string, page: number): Promise<BooksPage<BooksOpenDocument>>;
  /**
   * 🔒 One file a document lists, read by the call `BOOKS_ATTACHMENT_READS`
   * names for its kind (decision 3480). A kind with no read is never asked.
   */
  attachment(kind: BooksDocumentKind, documentId: string, attachmentId: string): Promise<BooksAttachmentFile>;
  /** 🔒 One page of the inventory adjustments dated inside the range, each read whole with its lines (decision 3660). */
  inventoryAdjustments(from: string, to: string, page: number): Promise<BooksPage<BooksInventoryAdjustment>>;
  /** 🔒 The pictures of the items named, as Zoho Inventory names them (decision 3661); an item carrying none is left out. */
  itemImages(itemIds: string[]): Promise<BooksItemImage[]>;
  /** 🔒 One item's picture, its bytes and its type as the answer gave them (decision 3661). */
  itemImage(itemId: string): Promise<BooksAttachmentFile>;
  /** 🔒 The stock of the items named, as Zoho Inventory's bulk item details answer it (decision 8260); an id it does not answer is left out. */
  itemStock(itemIds: string[]): Promise<BooksItemStock[]>;
  /** 🔒 The read's progress line hears each rate-limit wait as it starts, in milliseconds (T1233); a source that never waits leaves it out. */
  onRateWait?(listener: ((ms: number) => void) | null): void;
  /**
   * 🔒 One page of one bank or card account's lines dated inside the range, whatever their status (decision 8040). A
   * source that reads no bank transactions leaves it out, and block 5 refuses each account by name.
   */
  bankTransactions?(accountId: string, from: string, to: string, page: number): Promise<BooksPage<BooksBankTransaction>>;
  /** 🔒 One line read whole by its id, naming the account it was categorized to by id (decision 8040). */
  bankTransaction?(id: string, accountId: string): Promise<BooksBankTransaction>;
}

/**
 * 🔒 The status words of Zoho Books' bank transactions list, as the seam names them (decision 8040; the Bank
 * Transactions page, *Get transactions list*: uncategorized, manually_added, matched, excluded and categorized).
 */
export const BOOKS_BANK_TRANSACTION_STATUSES = ['Uncategorized', 'Categorized', 'ManuallyAdded', 'Matched', 'Excluded'] as const;
export type BooksBankTransactionStatus = (typeof BOOKS_BANK_TRANSACTION_STATUSES)[number];

/** 🔒 One line of a Zoho bank or card account (decision 8040), signed as a line here is: money into the account positive. */
export interface BooksBankTransaction {
  id: string;
  /** The Zoho bank or card account the line sits on. */
  accountId: string;
  date: string | null;
  /** Whole cents, signed: a deposit or a card payment positive, a withdrawal or a card charge negative; null where the source names no direction. */
  amountCents: number | null;
  /** Zoho's own transaction type, lower-cased: deposit, transfer_fund, card_payment and the rest. */
  type: string | null;
  /** Null where the source gave a word the list does not name. */
  status: BooksBankTransactionStatus | null;
  payee: string | null;
  description: string | null;
  reference: string | null;
  currencyCode: string | null;
  /** The account the line was categorized to, by name, as the list gives it. */
  offsetAccountName: string | null;
  /** The same account by its id, read only where a line is read whole; null on a list row. */
  offsetAccountId: string | null;
  /**
   * 🔒 The id of the expense, payment or journal the line stands for (decision 8820): `categorized_transaction_id`, the
   * one such field the reference prints, and null where the line carries none, when the block tries the line's own id.
   */
  documentId: string | null;
  /** 🔒 The line's distribution, one per account it was categorized to (decision 8820): filled on a whole read, empty on a list row. */
  lines: BooksBankTransactionLine[];
}

/**
 * 🔒 One account's share of a Zoho bank line (decision 8820), as *Get transaction*'s `line_items` and *Categorize*'s
 * print it. The amount is whole cents as Zoho prints the share: a share in the line's own direction is positive, so a
 * deposit's shares and a withdrawal's shares are each positive, and the block signs them by the line.
 */
export interface BooksBankTransactionLine {
  accountId: string | null;
  accountName: string | null;
  amountCents: number;
  taxId: string | null;
  /** The customer's or vendor's Zoho contact id. */
  contactId: string | null;
  description: string | null;
}
